import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileText, Phone, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "../lib/api";
import { Button, Card, Dl, ErrorBox, Field, Input, Select, Spinner, Textarea } from "../components/kd";
import { StatusBadge, UrgencyBadge } from "../components/Badges";
import { Chat, Timeline } from "../components/CaseParts";
import { ToothChart } from "../components/ToothChart";
import { DATEITYP, STATUS, VERSICHERUNG, WUNSCH, fmtDate, fmtDateTime, fmtSize } from "../lib/format";
import { DateInputDE, TimeInputDE } from "../components/DateInputs";
import { KiPreview, useKi, useKiText } from "../components/Ki";
import { useBausteine } from "../components/Snippets";

function Wiedervorlage({ f, reload }: { f: any; reload: () => void }) {
  const [datum, setDatum] = useState(f.wiedervorlage_am || "");
  const [notiz, setNotiz] = useState(f.wiedervorlage_notiz || "");
  const save = async (d: string | null) => {
    try { await api.put(`/praxis/faelle/${f.id}/wiedervorlage`, { datum: d, notiz }); toast.success(d ? "Wiedervorlage gespeichert." : "Wiedervorlage entfernt."); if (!d) { setDatum(""); setNotiz(""); } reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card data-testid="wiedervorlage-card">
      <h2 className="mb-3">Wiedervorlage</h2>
      {f.wiedervorlage_am && <p className="mb-3 text-sm" data-testid="wiedervorlage-current">Aktuell: {fmtDate(f.wiedervorlage_am)}{f.wiedervorlage_notiz ? ` – ${f.wiedervorlage_notiz}` : ""}</p>}
      <Field label="Wiedervorlage am" htmlFor="wv"><DateInputDE id="wv" value={datum} onChange={setDatum} className="max-w-[220px]" data-testid="wiedervorlage-date" /></Field>
      <Field label="Notiz" htmlFor="wvn"><Input id="wvn" maxLength={1000} value={notiz} onChange={(e) => setNotiz(e.target.value)} data-testid="wiedervorlage-notiz" /></Field>
      <div className="flex gap-2">
        <Button variant="secondary" disabled={!datum} onClick={() => save(datum)} data-testid="wiedervorlage-save">Speichern</Button>
        {f.wiedervorlage_am && <Button variant="ghost" onClick={() => save(null)} data-testid="wiedervorlage-clear">Entfernen</Button>}
      </div>
    </Card>
  );
}

function Kontaktversuche({ f, reload }: { f: any; reload: () => void }) {
  const bs = useBausteine();
  const [busy, setBusy] = useState(false);
  const kv = f.kontaktversuche || [];
  const text = bs.find((b) => b.kuerzel === "nichterreicht")?.text || "Wir haben Ihren Patienten trotz mehrerer Versuche telefonisch nicht erreicht. Bitte bitten Sie ihn, sich direkt bei uns zu melden.";
  const add = async () => { setBusy(true); try { await api.post(`/praxis/faelle/${f.id}/kontaktversuch`); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const [sent, setSent] = useState(false);
  const inform = async () => {
    if (!window.confirm("Diese Nachricht jetzt an den Überweiser senden?")) return;
    try { await api.post(`/praxis/faelle/${f.id}/nachrichten`, { text }); setSent(true); toast.success("Überweiser informiert."); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-5 border-t border-border pt-4" data-testid="kontaktversuche">
      <Button variant="secondary" loading={busy} onClick={add} data-testid="kontakt-nicht-erreicht"><Phone className="h-4 w-4" aria-hidden />Patient nicht erreicht{kv.length ? ` (${kv.length})` : ""}</Button>
      {kv.length > 0 && <ul className="mt-2 text-sm text-text-muted" data-testid="kontakt-liste">{kv.map((k: any, i: number) => <li key={i}>{i + 1}. Versuch: {fmtDateTime(k.zeit)}{k.benutzer ? ` · ${k.benutzer}` : ""}</li>)}</ul>}
      {kv.length >= 3 && (
        <div className="mt-3 rounded-lg border border-warning bg-warning-bg p-3 text-sm" data-testid="kontakt-vorschlag">
          <p className="mb-2 font-medium text-[#7A3E00]">{kv.length} erfolglose Versuche – Überweiser informieren?</p>
          <p className="mb-3">{text}</p>
          {sent ? <p className="font-medium text-success" data-testid="kontakt-informiert">Überweiser wurde informiert.</p> : <Button onClick={inform} data-testid="kontakt-informieren">Nachricht an Überweiser senden</Button>}
        </div>
      )}
    </div>
  );
}

function CaseSummary({ fid }: { fid: string }) {
  const ctx = { fall_id: fid };
  const flags = useKi(ctx);
  const kt = useKiText(ctx);
  if (!flags.assistent) return null;
  return (
    <Card data-testid="case-summary"><h2 className="mb-3">Zusammenfassung</h2>
      <Button variant="secondary" loading={!!kt.busy} onClick={() => kt.ask("zusammenfassung", "")} data-testid="case-summary-button"><Sparkles className="h-4 w-4" aria-hidden />Zusammenfassen</Button>
      {kt.suggestion && <KiPreview text={kt.suggestion.text} ctx={ctx} aktion="zusammenfassung" acceptLabel="Kopieren"
        onAccept={(t) => navigator.clipboard?.writeText(t).then(() => toast.success("In die Zwischenablage kopiert."))} onClose={kt.clear} />}
    </Card>
  );
}

function StatusActions({ d, reload }: { d: any; reload: () => void }) {
  const [sel, setSel] = useState<string | null>(null);
  const [kommentar, setKommentar] = useState("");
  const [tDate, setTDate] = useState("");
  const [tTime, setTTime] = useState("");
  const termin = tDate && /^([01]\d|2[0-3]):[0-5]\d$/.test(tTime) ? `${tDate}T${tTime}` : "";
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const needsComment = sel === "RUECKFRAGE" || sel === "ABGELEHNT";
  const submit = async () => {
    setErr("");
    if (needsComment && !kommentar.trim()) return setErr("Bitte geben Sie einen Kommentar an.");
    if (sel === "TERMIN_VEREINBART" && !termin) return setErr("Bitte geben Sie Datum (TT.MM.JJJJ) und Uhrzeit (HH:MM) des Termins an.");
    setBusy(true);
    try {
      await api.post(`/praxis/faelle/${d.fall.id}/status`, { zu_status: sel, kommentar: kommentar || null, termin_datum: termin || null });
      toast.success(`Status geändert: ${STATUS[sel as string]}`);
      setSel(null); setKommentar(""); setTDate(""); setTTime(""); reload();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d.naechste_status.length) return <p className="text-sm text-text-muted" data-testid="status-actions-none">Keine weiteren manuellen Statusänderungen möglich.</p>;
  return (
    <div data-testid="status-actions">
      <div className="flex flex-wrap gap-2">
        {d.naechste_status.map((s: string) => (
          <Button key={s} variant={sel === s ? "primary" : s === "ABGELEHNT" ? "danger" : "secondary"} onClick={() => { setSel(s); setErr(""); }} data-testid={`status-action-${s}`}>{STATUS[s]}</Button>
        ))}
      </div>
      {sel && (
        <div className="kd-fade mt-4 rounded-lg border border-border bg-bg p-4">
          <ErrorBox testid="status-action-error">{err}</ErrorBox>
          {(needsComment || sel !== "TERMIN_VEREINBART") && (
            <Field label={needsComment ? "Kommentar (Pflicht, für den Überweiser sichtbar)" : "Kommentar (optional)"} required={needsComment} htmlFor="sk">
              <Textarea id="sk" rows={3} value={kommentar} onChange={(e) => setKommentar(e.target.value)} data-testid="status-comment-input" />
            </Field>
          )}
          {sel === "TERMIN_VEREINBART" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Termin: Datum" required htmlFor="st"><DateInputDE id="st" value={tDate} onChange={setTDate} data-testid="status-termin-input" /></Field>
              <Field label="Uhrzeit" required htmlFor="stt"><TimeInputDE id="stt" value={tTime} onChange={setTTime} data-testid="status-termin-time-input" /></Field>
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={submit} loading={busy} data-testid="status-confirm-button">„{STATUS[sel]}“ bestätigen</Button>
            <Button variant="ghost" onClick={() => setSel(null)} data-testid="status-cancel-button">Abbrechen</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Assign({ d, reload }: { d: any; reload: () => void }) {
  const [team, setTeam] = useState<any[]>([]);
  useEffect(() => { api.get("/praxis/benutzer").then((r) => setTeam(r.data)); }, []);
  const change = async (v: string) => {
    try { await api.put(`/praxis/faelle/${d.fall.id}/zustaendig`, { benutzer_id: v || null }); toast.success("Zuständigkeit gespeichert."); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Field label="Zuständig" htmlFor="zust">
      <Select id="zust" options={team.map((t) => [t.id, t.name]) as any} placeholder="Nicht zugewiesen" value={d.fall.zustaendig_benutzer_id || ""} onChange={(e) => change(e.target.value)} data-testid="assign-select" />
    </Field>
  );
}

function Files({ files }: { files: any[] }) {
  if (!files.length) return <p className="text-text-muted">Keine Dateien vorhanden.</p>;
  return (
    <ul className="divide-y divide-border" data-testid="case-files">
      {files.map((f) => (
        <li key={f.id} className="flex items-center gap-3 py-3">
          {f.vorschau ? <img src={fileUrl(f.url)} alt="" className="h-14 w-14 shrink-0 rounded-md border border-border object-cover" /> : <FileText className="h-10 w-10 shrink-0 text-text-muted" aria-hidden />}
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{f.original_name}</p>
            <p className="text-sm text-text-muted">{DATEITYP[f.typ]} · {fmtSize(f.groesse_bytes)} · {f.hochgeladen_von === "PRAXIS" ? "Praxis" : "Überweiser"}</p>
          </div>
          <a href={fileUrl(f.url)} className="flex h-11 items-center gap-1 rounded-lg border border-primary px-3 text-primary no-underline hover:bg-primary-light" data-testid={`file-download-${f.id}`}><Download className="h-4 w-4" aria-hidden /><span className="hidden sm:inline">Download</span></a>
        </li>
      ))}
    </ul>
  );
}

export default function CaseDetail() {
  const { id } = useParams();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => { api.get(`/praxis/faelle/${id}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [id]);
  useEffect(() => { load(); }, [load]);
  if (err && !d) return <ErrorBox testid="case-error">{err}</ErrorBox>;
  if (!d) return <Spinner />;
  const f = d.fall, u = d.ueberweiser;
  return (
    <div className="kd-fade">
      <Link to="/praxis/faelle" className="mb-3 inline-flex min-h-[44px] items-center gap-1" data-testid="back-to-list"><ArrowLeft className="h-4 w-4" aria-hidden />Zur Eingangsliste</Link>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <h1 data-testid="case-fallnummer">{f.fallnummer}</h1>
        <StatusBadge status={f.status} testid="case-status-badge" />
        <UrgencyBadge value={f.dringlichkeit} testid="case-urgency-badge" />
        {d.unverifiziert && <span className="rounded-full border border-warning bg-warning-bg px-2.5 py-1 text-sm font-medium text-[#7A3E00]" data-testid="case-unverified">Überweiser nicht verifiziert</span>}
      </div>
      <div className="grid gap-5 lg:grid-cols-5">
        <div className="space-y-5 lg:col-span-3">
          <Card>
            <h2 className="mb-4">Patient</h2>
            <Dl items={[["Name", `${f.patient_vorname} ${f.patient_nachname}`], ["Geburtsdatum", fmtDate(f.patient_geburtsdatum)],
              ["Telefon", <a href={`tel:${f.patient_telefon}`}>{f.patient_telefon}</a>], ["E-Mail", f.patient_email], ["Versicherung", VERSICHERUNG[f.versicherung]]]} />
          </Card>
          <Card>
            <h2 className="mb-4">Überweisung</h2>
            <Dl items={[["Grund", d.grund_label], ["Beschreibung", f.grund_freitext], ["Behandlungswunsch", WUNSCH[f.behandlungswunsch]],
              ["Eingang", fmtDateTime(f.erstellt_am)], ["Termin", f.termin_datum ? fmtDateTime(f.termin_datum) : null], ["Bemerkung", f.bemerkung]]} />
            <h3 className="mb-2 mt-5">Zahnschema</h3>
            <ToothChart value={f.zaehne} readOnly />
          </Card>
          <Card>
            <h2 className="mb-4">Überweiser</h2>
            <Dl items={[["Praxis", u.praxisname], ["Zahnarzt", u.zahnarzt_name], ["Telefon", <a href={`tel:${u.telefon}`} className="inline-flex items-center gap-1" data-testid="ueberweiser-phone"><Phone className="h-4 w-4" aria-hidden />{u.telefon}</a>],
              ["E-Mail", <a href={`mailto:${u.email}`}>{u.email}</a>], ["Adresse", [u.strasse, `${u.plz} ${u.ort}`.trim()].filter(Boolean).join(", ")]]} />
          </Card>
          <Card><h2 className="mb-4">Dateien</h2><Files files={d.dateien} /></Card>
        </div>
        <div className="space-y-5 lg:col-span-2">
          <Card>
            <h2 className="mb-4">Aktionen</h2>
            <StatusActions d={d} reload={load} />
            <div className="mt-5"><Assign d={d} reload={load} /></div>
            {f.status === "ANGENOMMEN" && <Kontaktversuche f={f} reload={load} />}
            {d.bericht_moeglich && (
              <Link to={`/praxis/faelle/${f.id}/bericht`} className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-primary px-4 font-medium text-white no-underline hover:bg-primary-dark" data-testid="create-report-button">
                <FileText className="h-4 w-4" aria-hidden />{d.bericht?.versendet_am ? "Bericht ansehen" : "Bericht erstellen"}
              </Link>
            )}
          </Card>
          <Wiedervorlage key={f.wiedervorlage_am || "leer"} f={f} reload={load} />
          <Card><h2 className="mb-4">Zeitstrahl</h2><Timeline items={d.verlauf} /></Card>
          <CaseSummary fid={f.id} />
          <Card><h2 className="mb-4">Nachrichten</h2><Chat messages={d.nachrichten} me="PRAXIS" ki={{ fall_id: f.id }} onSend={async (text) => { await api.post(`/praxis/faelle/${f.id}/nachrichten`, { text }); load(); }} /></Card>
        </div>
      </div>
    </div>
  );
}
